import { AlertTriangle, CheckCircle, CheckSquare, Clock, Database, Edit, FileText, Plus, RefreshCw, Search, Square, Tag, Trash2, X, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import logger from '../lib/logger';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';

const PatentDatabaseComparisonManager = () => {
    const [patentFamilies, setPatentFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [comparisonResults, setComparisonResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [deletingIds, setDeletingIds] = useState(new Set());
    const [selectedDocIds, setSelectedDocIds] = useState(new Set());
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);
    const [editingKeywords, setEditingKeywords] = useState(null); // {docId: keywords_array}
    const [savingKeywords, setSavingKeywords] = useState(new Set());
    const hasFetched = useRef(false); // 防止重複請求
    const [initialLoading, setInitialLoading] = useState(true); // 初始載入狀態
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [displayMode, setDisplayMode] = useState('similar'); // 'similar' 或 'all'

    // 新增欄位管理狀態
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    const [fieldSelectorType, setFieldSelectorType] = useState(''); // 'boolean' 或 'classification'
    const [visibleBooleanFields, setVisibleBooleanFields] = useState(['titleKeywords', 'claimsKeywords', 'abstractKeywords', 'applicantKeywords']);
    const [visibleClassificationFields, setVisibleClassificationFields] = useState(['ipcClassification', 'cpcClassification']);

    const [advancedSearchForm, setAdvancedSearchForm] = useState({
        titleKeywords: '',
        claimsKeywords: '',
        abstractKeywords: '',
        applicantKeywords: '',
        applicantCountry: '',           // 申請人國別 (AXN)
        firstApplicant: '',             // 第一申請人
        inventorKeywords: '',           // 發明人 (IV)
        inventorCountry: '',            // 發明人國別 (IVN)
        applicationNumber: '',          // 申請號 (AN)
        publicationNumber: '',          // 公告號 (PN)
        agent: '',                      // 代理人
        examiner: '',                   // 審查委員 (EX)
        priorityNumber: '',             // 優先權號 (PR)
        citedPatent: '',                // 引用專利 (CI/CS)
        ipcClassification: '',          // IPC分類 (IC)
        firstIpc: '',                   // 第一IPC (IQ)
        cpcClassification: '',          // CPC分類 (CI)
        locClassification: '',          // LOC分類
        uspcClassification: '',         // USPC分類
        fiClassification: '',           // FI分類
        ftermClassification: '',        // F-TERM分類
        dtermClassification: '',        // D-TERM分類
        dateFrom: '',
        dateTo: '',
        databases: ['TW'],
        patentTypes: ['I'],
        outputFormat: 'xml',
        maxResults: 100
    });

    // 欄位配置定義
    const booleanFieldConfig = {
        titleKeywords: { label: '專利名稱 (TI)', placeholder: '輸入專利名稱關鍵字' },
        claimsKeywords: { label: '申請專利範圍 (CL)', placeholder: '輸入專利範圍關鍵字' },
        abstractKeywords: { label: '摘要 (AB)', placeholder: '輸入摘要關鍵字' },
        applicantKeywords: { label: '申請人 (PA)', placeholder: '輸入申請人關鍵字' },
        applicantCountry: { label: '申請人國別 (AXN)', placeholder: '例如: TW, US, JP' },
        firstApplicant: { label: '第一申請人', placeholder: '輸入第一申請人名稱' },
        inventorKeywords: { label: '發明人 (IV)', placeholder: '輸入發明人關鍵字' },
        inventorCountry: { label: '發明人國別 (IVN)', placeholder: '例如: TW, US, JP' },
        applicationNumber: { label: '申請號 (AN)', placeholder: '例如: 112345678' },
        publicationNumber: { label: '公告號 (PN)', placeholder: '例如: I765432' },
        agent: { label: '代理人', placeholder: '輸入代理人名稱' },
        examiner: { label: '審查委員 (EX)', placeholder: '輸入審查委員名稱' },
        priorityNumber: { label: '優先權號 (PR)', placeholder: '例如: US2023123456' },
        citedPatent: { label: '引用專利 (CS/CI)', placeholder: '輸入引用專利號' }
    };

    const classificationFieldConfig = {
        ipcClassification: { label: 'IPC分類 (IC)', placeholder: '例: H04L29/06' },
        firstIpc: { label: '第一IPC (IQ)', placeholder: '例: H04L' },
        cpcClassification: { label: 'CPC分類 (CI)', placeholder: '例: H04L63/00' },
        uspcClassification: { label: 'USPC分類', placeholder: '例: 709/224' },
        fiClassification: { label: 'FI分類', placeholder: '日本FI分類' },
        ftermClassification: { label: 'F-TERM分類', placeholder: '日本F-TERM分類' },
        dtermClassification: { label: 'D-TERM分類', placeholder: '設計術語分類' },
        locClassification: { label: 'LOC分類', placeholder: '輸入LOC分類' }
    };

    const handleAddField = (fieldKey) => {
        if (fieldSelectorType === 'boolean') {
            setVisibleBooleanFields(prev => [...prev, fieldKey]);
        } else {
            setVisibleClassificationFields(prev => [...prev, fieldKey]);
        }
        setShowFieldSelector(false);
    };

    const handleRemoveField = (fieldKey, type) => {
        if (type === 'boolean') {
            setVisibleBooleanFields(prev => prev.filter(f => f !== fieldKey));
        } else {
            setVisibleClassificationFields(prev => prev.filter(f => f !== fieldKey));
        }
        // 清空該欄位的值
        setAdvancedSearchForm(prev => ({ ...prev, [fieldKey]: '' }));
    };

    useEffect(() => {
        // 只在第一次掛載時執行
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchPatentFamilies();
        }
    }, []);

    const fetchPatentFamilies = async () => {
        try {
            setIsLoading(true);
            // 從文件分析功能獲取已分析的文件
            const response = await fetch('/api/documents/analyzed-documents');
            const data = await response.json();

            if (data.success && Array.isArray(data.data)) {
                setPatentFamilies(data.data);
            } else {
                // 當沒有已分析的文件時，顯示空列表
                setPatentFamilies([]);
            }
        } catch (error) {
            logger.error('獲取已分析文件列表失敗:', error);
            setPatentFamilies([]);
        } finally {
            setIsLoading(false);
            setInitialLoading(false); // 初始載入完成
        }
    };

    const handleDeleteDocument = async (docId, event) => {
        // 強制停止事件傳播
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!confirm('確定要刪除這個已分析的文件嗎？此操作無法撤銷。')) {
            return;
        }

        try {
            setDeletingIds(prev => new Set([...prev, docId]));

            const response = await fetch(`/api/documents/analyzed-documents/${docId}`, {
                method: 'DELETE'
            });

            const result = await response.json();

            if (result.success) {
                // 從列表中移除已刪除的文件
                setPatentFamilies(prev => prev.filter(f => f.doc_id !== docId));

                // 如果刪除的是當前選中的文件，清除選擇
                if (selectedFamily?.doc_id === docId) {
                    setSelectedFamily(null);
                    setComparisonResults(null);
                }

                alert(result.message || '文件已成功刪除');
            } else {
                alert('刪除失敗: ' + (result.error || '未知錯誤'));
            }
        } catch (error) {
            logger.error('刪除文件失敗:', error);
            alert('刪除文件時發生錯誤');
        } finally {
            setDeletingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(docId);
                return newSet;
            });
        }
    };

    const handleBatchDelete = async () => {
        if (selectedDocIds.size === 0) {
            alert('請先選擇要刪除的文件');
            return;
        }

        if (!confirm(`確定要刪除選中的 ${selectedDocIds.size} 個文件嗎？此操作無法撤銷。`)) {
            return;
        }

        try {
            setIsBatchDeleting(true);

            const docIdsArray = Array.from(selectedDocIds);
            logger.info('批量刪除 - 選中的 IDs:', docIdsArray);
            logger.info('批量刪除 - IDs 類型:', docIdsArray.map(id => `${id} (${typeof id})`));

            const response = await fetch('/api/documents/analyzed-documents/batch-delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    doc_ids: docIdsArray
                })
            });

            const result = await response.json();

            if (result.success) {
                // 從列表中移除已刪除的文件
                setPatentFamilies(prev => prev.filter(f => !selectedDocIds.has(f.doc_id)));

                // 如果刪除的包含當前選中的文件，清除選擇
                if (selectedFamily && selectedDocIds.has(selectedFamily.doc_id)) {
                    setSelectedFamily(null);
                    setComparisonResults(null);
                }

                // 清空選擇
                setSelectedDocIds(new Set());

                alert(result.message || `成功刪除 ${result.deleted_count} 個文件`);
            } else {
                alert('批量刪除失敗: ' + (result.error || '未知錯誤'));
            }
        } catch (error) {
            logger.error('批量刪除文件失敗:', error);
            alert('批量刪除文件時發生錯誤');
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedDocIds.size === patentFamilies.length) {
            // 全部已選中，取消全選
            setSelectedDocIds(new Set());
        } else {
            // 全選
            setSelectedDocIds(new Set(patentFamilies.map(f => f.doc_id)));
        }
    };

    const toggleSelectDoc = (docId, event) => {
        event.stopPropagation(); // 防止觸發選擇事件
        setSelectedDocIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(docId)) {
                newSet.delete(docId);
            } else {
                newSet.add(docId);
            }
            return newSet;
        });
    };

    const handleUpdateKeywords = async (docId) => {
        const keywords = editingKeywords;
        if (!keywords || !Array.isArray(keywords)) {
            alert('請輸入有效的關鍵字');
            return;
        }

        try {
            setSavingKeywords(prev => new Set([...prev, docId]));

            const response = await fetch(`/api/document/update-keywords/${docId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ keywords })
            });

            const result = await response.json();

            if (result.success) {
                // 更新本地狀態
                setPatentFamilies(prev => prev.map(f =>
                    f.doc_id === docId
                        ? { ...f, user_keywords: keywords, keywords: keywords }
                        : f
                ));
                setEditingKeywords(null);
                alert('關鍵字更新成功！');
            } else {
                alert('更新失敗: ' + (result.message || '未知錯誤'));
            }
        } catch (error) {
            logger.error('更新關鍵字失敗:', error);
            alert('更新關鍵字時發生錯誤');
        } finally {
            setSavingKeywords(prev => {
                const newSet = new Set(prev);
                newSet.delete(docId);
                return newSet;
            });
        }
    };

    const startDatabaseComparison = async (familyId) => {
        setIsLoading(true);
        try {
            // 根據familyId找到完整的案件數據
            const family = patentFamilies.find(f => f.id === familyId);
            if (!family) {
                logger.error('找不到指定的專利家族');
                setIsLoading(false);
                return;
            }

            // 記錄 tracking_id 用於調試
            logger.info('📊 開始專利比對, family:', family);
            logger.info('📊 tracking_id:', family.tracking_id);

            const response = await fetch('/api/patent/tasks/patent-database-comparison', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    case_list: [{
                        case_id: family.id,
                        title: family.title,
                        keywords: family.ai_keywords || [],  // 傳遞AI生成的關鍵字
                        priority_number: family.priority_number,
                        priority_date: family.priority_date,
                        applicant: family.applicant,
                        inventor: family.inventor,
                        countries: family.countries
                    }],
                    search_mode: 'quick_search',  // 快速檢索模式
                    keyword_search: true,
                    similarity_threshold: 0.7,
                    target_countries: ['TW'],  // 只搜索台灣資料庫
                    tracking_id: family.tracking_id  // 傳遞 KPI 追蹤 ID
                }),
            });

            const result = await response.json();
            if (result.success && result.status === 'completed') {
                logger.info('✅ 專利比對完成, 找到', result.statistics?.total_patents_found, '筆專利');
                
                // 直接處理即時返回的結果
                let allPatents = [];
                if (result.search_results && Array.isArray(result.search_results)) {
                    result.search_results.forEach(caseResult => {
                        if (caseResult.search_results && Array.isArray(caseResult.search_results)) {
                            allPatents = allPatents.concat(caseResult.search_results);
                        }
                    });
                }

                setComparisonResults({
                    summary: {
                        search_mode: result.statistics?.search_mode || 'quick_search',
                        total_patents_found: allPatents.length,
                        similar_patents_found: result.statistics?.similar_patents_found || 0
                    },
                    search_results: allPatents,  // 修正: 使用 search_results 而非 patents
                    report: result.report,
                    notifications: result.notifications
                });
                setIsLoading(false);
            } else {
                logger.error('資料庫比對失敗:', result.error);
                setIsLoading(false);
            }
        } catch (error) {
            logger.error('資料庫比對錯誤:', error);
            setIsLoading(false);
        }
    };

    // 進階搜索功能
    const startAdvancedSearch = async () => {
        setShowAdvancedSearch(false); // 關閉表單
        setIsLoading(true);

        try {
            if (!selectedFamily) {
                logger.error('未選擇專利家族');
                setIsLoading(false);
                return;
            }

            // 構建 custom_params,完全過濾空值,使用後端期望的欄位名稱
            const customParams = {
                databases: advancedSearchForm.databases,
                patent_types: advancedSearchForm.patentTypes,
                output_format: advancedSearchForm.outputFormat,
                max_results: advancedSearchForm.maxResults
            };

            // 只添加非空的搜索欄位
            if (advancedSearchForm.titleKeywords?.trim()) {
                customParams.title_keywords = advancedSearchForm.titleKeywords.trim();
            }
            if (advancedSearchForm.claimsKeywords?.trim()) {
                customParams.claims_keywords = advancedSearchForm.claimsKeywords.trim();
            }
            if (advancedSearchForm.abstractKeywords?.trim()) {
                customParams.abstract_keywords = advancedSearchForm.abstractKeywords.trim();
            }
            if (advancedSearchForm.applicantKeywords?.trim()) {
                customParams.applicant_keywords = advancedSearchForm.applicantKeywords.trim();
            }
            if (advancedSearchForm.applicantCountry?.trim()) {
                customParams.applicant_country = advancedSearchForm.applicantCountry.trim();
            }
            if (advancedSearchForm.firstApplicant?.trim()) {
                customParams.first_applicant = advancedSearchForm.firstApplicant.trim();
            }
            if (advancedSearchForm.inventorKeywords?.trim()) {
                customParams.inventor_keywords = advancedSearchForm.inventorKeywords.trim();
            }
            if (advancedSearchForm.inventorCountry?.trim()) {
                customParams.inventor_country = advancedSearchForm.inventorCountry.trim();
            }
            if (advancedSearchForm.applicationNumber?.trim()) {
                customParams.application_number = advancedSearchForm.applicationNumber.trim();
            }
            if (advancedSearchForm.publicationNumber?.trim()) {
                customParams.publication_number = advancedSearchForm.publicationNumber.trim();
            }
            if (advancedSearchForm.agent?.trim()) {
                customParams.agent = advancedSearchForm.agent.trim();
            }
            if (advancedSearchForm.examiner?.trim()) {
                customParams.examiner = advancedSearchForm.examiner.trim();
            }
            if (advancedSearchForm.priorityNumber?.trim()) {
                customParams.priority_number = advancedSearchForm.priorityNumber.trim();
            }
            if (advancedSearchForm.citedPatent?.trim()) {
                customParams.cited_patent = advancedSearchForm.citedPatent.trim();
            }
            if (advancedSearchForm.ipcClassification?.trim()) {
                customParams.ipc_classification = advancedSearchForm.ipcClassification.trim();
            }
            if (advancedSearchForm.firstIpc?.trim()) {
                customParams.first_ipc = advancedSearchForm.firstIpc.trim();
            }
            if (advancedSearchForm.cpcClassification?.trim()) {
                customParams.cpc_classification = advancedSearchForm.cpcClassification.trim();
            }
            if (advancedSearchForm.locClassification?.trim()) {
                customParams.loc_classification = advancedSearchForm.locClassification.trim();
            }
            if (advancedSearchForm.uspcClassification?.trim()) {
                customParams.uspc_classification = advancedSearchForm.uspcClassification.trim();
            }
            if (advancedSearchForm.fiClassification?.trim()) {
                customParams.fi_classification = advancedSearchForm.fiClassification.trim();
            }
            if (advancedSearchForm.ftermClassification?.trim()) {
                customParams.fterm_classification = advancedSearchForm.ftermClassification.trim();
            }
            if (advancedSearchForm.dtermClassification?.trim()) {
                customParams.dterm_classification = advancedSearchForm.dtermClassification.trim();
            }

            // 只有在有值時才添加日期參數
            if (advancedSearchForm.dateFrom?.trim()) {
                customParams.date_from = advancedSearchForm.dateFrom.trim();
            }
            if (advancedSearchForm.dateTo?.trim()) {
                customParams.date_to = advancedSearchForm.dateTo.trim();
            }

            logger.info('🔬 提交進階搜索請求:', {
                case_id: selectedFamily.id,
                title: selectedFamily.title,
                custom_params: customParams
            });

            const response = await fetch('/api/patent/tasks/patent-database-comparison', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    case_list: [{
                        case_id: selectedFamily.id,
                        title: selectedFamily.title,
                        custom_params: customParams
                    }],
                    search_mode: 'advanced_custom',
                    tracking_id: selectedFamily.tracking_id
                }),
            });

            const result = await response.json();
            if (result.success && result.status === 'completed') {
                logger.info('✅ 進階搜索完成, 找到', result.statistics?.total_patents_found, '筆專利');
                
                // 直接處理即時返回的結果
                let allPatents = [];
                if (result.search_results && Array.isArray(result.search_results)) {
                    result.search_results.forEach(caseResult => {
                        if (caseResult.search_results && Array.isArray(caseResult.search_results)) {
                            allPatents = allPatents.concat(caseResult.search_results);
                        }
                    });
                }

                setComparisonResults({
                    summary: {
                        search_mode: result.statistics?.search_mode || 'advanced_custom',
                        total_patents_found: allPatents.length,
                        similar_patents_found: result.statistics?.similar_patents_found || 0
                    },
                    search_results: allPatents,  // 修正: 使用 search_results 而非 patents
                    report: result.report,
                    notifications: result.notifications
                });
                setIsLoading(false);
            } else {
                logger.error('進階搜索失敗:', result.error);
                setIsLoading(false);
            }
        } catch (error) {
            logger.error('進階搜索錯誤:', error);
            setIsLoading(false);
        }
    };

    const getCountryFlag = (countryCode) => {
        const flags = {
            'TW': '🇹🇼',
            'US': '🇺🇸',
            'EP': '🇪🇺',
            'JP': '🇯🇵',
            'CN': '🇨🇳'
        };
        return flags[countryCode] || '🌐';
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'analyzed':
                return <Badge variant="success">已分析</Badge>;
            case 'active':
                return <Badge variant="success">活躍</Badge>;
            case 'pending':
                return <Badge variant="secondary">待處理</Badge>;
            case 'expired':
                return <Badge variant="destructive">已過期</Badge>;
            default:
                return <Badge variant="outline">未知</Badge>;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">TIPO專利資料庫檢索</h1>
                    <p className="text-muted-foreground">
                        從文件分析功能上傳並分析文件後，在此進行TIPO專利資料庫檢索比對
                    </p>
                </div>
                <Button onClick={fetchPatentFamilies} disabled={isLoading}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    重新整理
                </Button>
            </div>

            {/* 載入中 - 初始載入時立即顯示 */}
            {initialLoading && (
                <Card>
                    <CardContent className="p-12">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="relative">
                                {/* 旋轉圈圈 */}
                                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
                                {/* 脈衝效果 */}
                                <div className="absolute inset-0 rounded-full border-4 border-blue-200 animate-pulse"></div>
                            </div>
                            <div className="text-center space-y-2">
                                <p className="text-lg font-medium text-gray-700">載入已分析文件列表中...</p>
                                <p className="text-sm text-gray-500">請稍候片刻</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {patentFamilies.length === 0 && !isLoading && !initialLoading && (
                <Card>
                    <CardContent className="p-8">
                        <div className="text-center text-muted-foreground">
                            <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-medium mb-2">尚無已分析的文件</p>
                            <p className="text-sm">請先到「文件分析」功能上傳並分析專利文件</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {patentFamilies.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* 已分析文件列表 - 佔1/3寬度 */}
                    <Card className="border-0 shadow-lg">
                        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b">
                            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                <div className="p-2 bg-blue-500 rounded-lg">
                                    <FileText className="h-5 w-5 text-white" />
                                </div>
                                已分析文件列表
                            </CardTitle>
                            <CardDescription className="text-gray-600 dark:text-gray-400">
                                選擇已分析的專利文件進行TIPO資料庫檢索
                            </CardDescription>
                            {/* 批量操作工具欄 - 現代化設計 */}
                            <div className="flex items-center gap-3 pt-4 mt-3 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={toggleSelectAll}
                                    className="flex items-center gap-2 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                >
                                    {selectedDocIds.size === patentFamilies.length && patentFamilies.length > 0 ? (
                                        <CheckSquare className="h-4 w-4 text-blue-600" />
                                    ) : (
                                        <Square className="h-4 w-4 text-gray-400" />
                                    )}
                                    {selectedDocIds.size === patentFamilies.length && patentFamilies.length > 0 ? '取消全選' : '全選'}
                                </Button>
                                {selectedDocIds.size > 0 && (
                                    <>
                                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-4 py-1.5 rounded-full shadow-sm">
                                            已選擇 {selectedDocIds.size} 項
                                        </span>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={handleBatchDelete}
                                            disabled={isBatchDeleting}
                                            className="ml-auto font-medium shadow-md hover:shadow-lg transition-shadow"
                                        >
                                            {isBatchDeleting ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    刪除中...
                                                </div>
                                            ) : (
                                                <>
                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                    批量刪除
                                                </>
                                            )}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="h-[600px] overflow-y-auto p-6 pt-5 bg-gray-50/50 dark:bg-gray-900/20">
                            <div className="space-y-4">
                                {patentFamilies.map((family) => (
                                    <Card
                                        key={family.id}
                                        className={`cursor-pointer transition-all duration-200 hover:shadow-md border ${selectedFamily?.id === family.id
                                            ? 'ring-2 ring-blue-500 shadow-lg bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-800'
                                            : 'hover:border-blue-200 dark:hover:border-blue-700 bg-white dark:bg-gray-800'
                                            }`}
                                        onClick={() => setSelectedFamily(family)}
                                    >
                                        <CardContent className="p-4">
                                            <div className="space-y-3">
                                                {/* 頂部：選擇框 + 標題 + 刪除按鈕 */}
                                                <div className="flex items-start gap-3">
                                                    {/* 選擇框 */}
                                                    <div
                                                        className="flex items-center pt-0.5 cursor-pointer hover:scale-110 transition-transform flex-shrink-0"
                                                        onClick={(e) => toggleSelectDoc(family.doc_id, e)}
                                                        title="點擊選擇此文件"
                                                    >
                                                        {selectedDocIds.has(family.doc_id) ? (
                                                            <CheckSquare className="h-5 w-5 text-blue-600" />
                                                        ) : (
                                                            <Square className="h-5 w-5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400" />
                                                        )}
                                                    </div>

                                                    {/* 標題 */}
                                                    <h4 className="font-semibold text-sm leading-snug flex-1 text-center text-gray-900 dark:text-gray-100" title={family.original_filename || family.filename}>
                                                        {family.title}
                                                    </h4>

                                                    {/* 刪除按鈕 */}
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                            onClick={(e) => handleDeleteDocument(family.doc_id, e)}
                                                            disabled={deletingIds.has(family.doc_id)}
                                                            title="刪除文件"
                                                        >
                                                            {deletingIds.has(family.doc_id) ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                                            ) : (
                                                                <Trash2 className="h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* 原始檔名 */}
                                                {family.original_filename && family.original_filename !== family.title && (
                                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-md">
                                                        <FileText className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                                            {family.original_filename}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* 信息標籤 */}
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-blue-500"></div>
                                                        <span className="font-medium">ID:</span>
                                                        <span className="truncate">{family.priority_number}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
                                                        <span className="font-medium">申請人:</span>
                                                        <span className="truncate">{family.applicant}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    <span>{family.priority_date || family.analyzed_at}</span>
                                                </div>

                                                {/* 關鍵字顯示和編輯 - 現代化設計 */}
                                                {family.keywords && family.keywords.length > 0 && (
                                                    <div className="mt-3 space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Tag className="h-3.5 w-3.5 text-indigo-500" />
                                                                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                                                    關鍵字
                                                                    <span className="ml-1.5 text-indigo-600 dark:text-indigo-400">
                                                                        {family.user_keywords && family.user_keywords.length > 0 ? '(已編輯)' : '(AI生成)'}
                                                                    </span>
                                                                </p>
                                                            </div>
                                                            {selectedFamily?.id === family.id && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 font-medium"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditingKeywords(family.keywords);
                                                                    }}
                                                                >
                                                                    <Edit className="h-3 w-3 mr-1" />
                                                                    編輯
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {family.keywords.map((keyword, idx) => (
                                                                <Badge
                                                                    key={idx}
                                                                    variant="secondary"
                                                                    className="text-xs px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                                                                >
                                                                    {keyword}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* 關鍵字編輯框 - 現代化設計 */}
                                                {editingKeywords && selectedFamily?.id === family.id && (
                                                    <div className="mt-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl space-y-3 shadow-sm" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1.5 bg-blue-500 rounded-lg">
                                                                <Edit className="h-3.5 w-3.5 text-white" />
                                                            </div>
                                                            <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">編輯關鍵字 (用逗號分隔)</p>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className="w-full px-3 py-2.5 text-sm border-2 border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-100 transition-all shadow-sm"
                                                            defaultValue={editingKeywords.join(', ')}
                                                            onChange={(e) => {
                                                                const keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k);
                                                                setEditingKeywords(keywords);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            placeholder="輸入關鍵字,用逗號分隔..."
                                                        />
                                                        <div className="flex gap-2 pt-1">
                                                            <Button
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleUpdateKeywords(family.doc_id);
                                                                }}
                                                                disabled={savingKeywords.has(family.doc_id)}
                                                                className="flex-1 bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all"
                                                            >
                                                                {savingKeywords.has(family.doc_id) ? (
                                                                    <>
                                                                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-2"></div>
                                                                        儲存中...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                                                        儲存
                                                                    </>
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingKeywords(null);
                                                                }}
                                                                className="flex-1 border-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                                            >
                                                                <X className="h-3.5 w-3.5 mr-1.5" />
                                                                取消
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                {family.extracted_text && (
                                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                                        摘要: {family.extracted_text}
                                                    </p>
                                                )}
                                                <div className="flex items-center gap-1">
                                                    {family.countries.map(country => (
                                                        <span key={country} className="text-lg">
                                                            {getCountryFlag(country)}
                                                        </span>
                                                    ))}
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    {getStatusBadge(family.status)}
                                                    <span className="text-xs text-muted-foreground">
                                                        {family.file_type || 'document'}
                                                    </span>
                                                </div>
                                            </div>
                                            {selectedFamily?.id === family.id && (
                                                <div className="mt-4 pt-4 border-t space-y-2">
                                                    <Button
                                                        onClick={() => startDatabaseComparison(family.id)}
                                                        disabled={isLoading}
                                                        size="sm"
                                                        className="w-full"
                                                    >
                                                        {isLoading ? '檢索中...' : '快速檢索'}
                                                    </Button>
                                                    <Button
                                                        onClick={() => {
                                                            // 自動填充AI關鍵字
                                                            const aiKeywords = family.ai_keywords || [];
                                                            const clKeywords = aiKeywords.join(' OR ');
                                                            setAdvancedSearchForm({
                                                                ...advancedSearchForm,
                                                                claimsKeywords: clKeywords
                                                            });
                                                            setShowAdvancedSearch(true);
                                                        }}
                                                        disabled={isLoading}
                                                        size="sm"
                                                        variant="outline"
                                                        className="w-full"
                                                    >
                                                        <Search className="h-4 w-4 mr-2" />
                                                        進階搜索表單
                                                    </Button>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* 比對結果 - 佔2/3寬度 */}
                    <Card className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle>檢索結果</CardTitle>
                            <CardDescription>
                                TIPO完整專利資料庫檢索比對分析結果
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="h-[600px] overflow-y-auto">
                            {isLoading ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                        <span>執行台灣外國專利檢索中...</span>
                                    </div>
                                    <Progress value={60} />
                                    <p className="text-sm text-muted-foreground">
                                        正在連接台灣智慧財產局完整資料庫並檢索專利資料...
                                    </p>
                                </div>
                            ) : comparisonResults ? (
                                <div className="space-y-6">
                                    {comparisonResults.error ? (
                                        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-600 rounded-lg">
                                            <div className="flex items-center gap-2 text-red-600 dark:text-red-300 mb-2">
                                                <XCircle className="h-5 w-5" />
                                                <span className="font-medium">比對失敗</span>
                                            </div>
                                            <p className="text-sm text-red-600 dark:text-red-200">{comparisonResults.error}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* 比對統計 */}
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border dark:border-blue-700">
                                                    <p className="text-sm font-medium text-blue-600 dark:text-blue-300">檢索模式</p>
                                                    <p className="text-lg font-bold text-blue-700 dark:text-blue-200">
                                                        {comparisonResults.summary?.search_mode === 'quick_search' ? '🚀 快速檢索' :
                                                            comparisonResults.summary?.search_mode === 'advanced_custom' ? '🔬 進階搜索' :
                                                                comparisonResults.summary?.search_mode || 'comprehensive'}
                                                    </p>
                                                </div>
                                                <div
                                                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${displayMode === 'similar'
                                                        ? 'bg-purple-100 dark:bg-purple-800 border-purple-500 dark:border-purple-400 ring-2 ring-purple-500'
                                                        : 'bg-purple-50 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 hover:border-purple-400'
                                                        }`}
                                                    onClick={() => setDisplayMode('similar')}
                                                    title="點擊查看相似專利"
                                                >
                                                    <p className="text-sm font-medium text-purple-600 dark:text-purple-300">相似專利</p>
                                                    <p className="text-lg font-bold text-purple-700 dark:text-purple-200">
                                                        {comparisonResults.summary?.similar_patents_found || 0}
                                                    </p>
                                                </div>
                                                <div
                                                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${displayMode === 'all'
                                                        ? 'bg-green-100 dark:bg-green-800 border-green-500 dark:border-green-400 ring-2 ring-green-500'
                                                        : 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 hover:border-green-400'
                                                        }`}
                                                    onClick={() => setDisplayMode('all')}
                                                    title="點擊查看所有輸出專利"
                                                >
                                                    <p className="text-sm font-medium text-green-600 dark:text-green-300">輸出專利數量</p>
                                                    <p className="text-lg font-bold text-green-700 dark:text-green-200">
                                                        {comparisonResults.summary?.total_patents_found || 0}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* 檢索結果詳情 - 根據 displayMode 顯示不同的專利 */}
                                            {comparisonResults.search_results && comparisonResults.search_results.length > 0 && (
                                                <div className="space-y-4">
                                                    <h4 className="font-medium">
                                                        {displayMode === 'similar' ? '相似專利列表' : 'TIPO完整專利資料庫檢索結果'}
                                                    </h4>
                                                    <div className="space-y-2">
                                                        {comparisonResults.search_results
                                                            .filter(patent => displayMode === 'all' || patent.similarity_score >= 0.7)
                                                            .map((patent, patentIdx) => {
                                                                // 構建 Google Patents URL
                                                                const googlePatentsUrl = patent.publication_number
                                                                    ? `https://patents.google.com/patent/${patent.publication_number.replace(/\s+/g, '')}`
                                                                    : null;

                                                                return (
                                                                    <div
                                                                        key={patentIdx}
                                                                        className="p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500 transition-all cursor-pointer"
                                                                        onClick={() => googlePatentsUrl && window.open(googlePatentsUrl, '_blank')}
                                                                        title="點擊查看 Google Patents 詳細資訊"
                                                                    >
                                                                        <div className="space-y-2">
                                                                            <div className="flex items-start justify-between">
                                                                                <div className="flex-1">
                                                                                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-2 leading-tight">{patent.title}</p>
                                                                                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                                                                                        專利號: {patent.publication_number} |
                                                                                        申請號: {patent.foreign_application_number}
                                                                                    </p>
                                                                                    {patent.priority_data && (
                                                                                        <p className="text-xs text-gray-700 dark:text-gray-300">
                                                                                            優先權號: {patent.priority_data}
                                                                                        </p>
                                                                                    )}
                                                                                    <p className="text-xs text-gray-700 dark:text-gray-300">
                                                                                        申請人: {patent.applicant}
                                                                                    </p>
                                                                                    <p className="text-xs text-gray-700 dark:text-gray-300">
                                                                                        發明人: {patent.inventor}
                                                                                    </p>
                                                                                    <p className="text-xs text-gray-700 dark:text-gray-300">
                                                                                        公開日: {patent.publication_date} |
                                                                                        來源國: {patent.origin_country}
                                                                                    </p>
                                                                                </div>
                                                                                <div className="ml-4 text-right flex flex-col items-end gap-2 flex-shrink-0">
                                                                                    {patent.search_keyword && (
                                                                                        <Badge
                                                                                            variant="secondary"
                                                                                            className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 cursor-help max-w-xs break-words text-right"
                                                                                            title={`搜尋條件：${patent.search_keyword}`}
                                                                                        >
                                                                                            🔍 {patent.search_keyword}
                                                                                        </Badge>
                                                                                    )}
                                                                                    {patent.similarity_score != null && (
                                                                                        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                                                            相似度: {(patent.similarity_score * 100).toFixed(1)}%
                                                                                        </p>
                                                                                    )}
                                                                                    {googlePatentsUrl && (
                                                                                        <Badge variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                                                                                            點擊查看詳情 →
                                                                                        </Badge>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            {patent.abstract && (
                                                                                <div className="text-xs text-gray-800 dark:text-gray-200 mt-2 bg-gray-50 dark:bg-gray-600 p-3 rounded leading-relaxed text-justify">
                                                                                    <span className="font-medium">摘要:</span> {patent.abstract}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* 功能說明 */}
                                            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-600 rounded-lg">
                                                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-300 mb-2">
                                                    <AlertTriangle className="h-5 w-5" />
                                                    <span className="font-medium">TIPO快速檢索功能</span>
                                                </div>
                                                <p className="text-sm text-blue-700 dark:text-blue-200">
                                                    系統使用台灣智慧財產局(TIPO)真實API進行快速檢索。
                                                    快速檢索模式：使用AI生成的關鍵字搜索專利範圍(Claims)，只搜索台灣專利資料庫(TW)，預設返回30筆最相關結果。
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center text-muted-foreground py-8">
                                    <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>請選擇專利案件並執行TIPO完整資料庫檢索</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* 進階搜索 Modal */}
            {showAdvancedSearch && selectedFamily && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                    進階搜索表單
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {selectedFamily.title}
                                </p>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowAdvancedSearch(false)}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* 資料庫選擇 */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">資料庫選擇</Label>
                                <div className="grid grid-cols-3 gap-4">
                                    {[
                                        { value: 'TW', label: '台灣 (TW)' },
                                        { value: 'US', label: '美國 (US)' },
                                        { value: 'EU', label: '歐盟 (EU)' },
                                        { value: 'JP', label: '日本 (JP)' },
                                        { value: 'CN', label: '中國 (CN)' },
                                        { value: 'KR', label: '韓國 (KR)' }
                                    ].map(db => (
                                        <div key={db.value} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`db-${db.value}`}
                                                checked={advancedSearchForm.databases.includes(db.value)}
                                                onCheckedChange={(checked) => {
                                                    setAdvancedSearchForm(prev => ({
                                                        ...prev,
                                                        databases: checked
                                                            ? [...prev.databases, db.value]
                                                            : prev.databases.filter(d => d !== db.value)
                                                    }));
                                                }}
                                            />
                                            <Label htmlFor={`db-${db.value}`} className="cursor-pointer">
                                                {db.label}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* 專利類型選擇 */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">專利類型</Label>
                                <div className="grid grid-cols-3 gap-4">
                                    {[
                                        { value: 'I', label: '發明 (I)' },
                                        { value: 'M', label: '新型 (M)' },
                                        { value: 'D', label: '設計 (D)' }
                                    ].map(type => (
                                        <div key={type.value} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`type-${type.value}`}
                                                checked={advancedSearchForm.patentTypes.includes(type.value)}
                                                onCheckedChange={(checked) => {
                                                    setAdvancedSearchForm(prev => ({
                                                        ...prev,
                                                        patentTypes: checked
                                                            ? [...prev.patentTypes, type.value]
                                                            : prev.patentTypes.filter(t => t !== type.value)
                                                    }));
                                                }}
                                            />
                                            <Label htmlFor={`type-${type.value}`} className="cursor-pointer">
                                                {type.label}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* 布林檢索 */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">布林檢索</Label>
                                <div className="grid grid-cols-2 gap-4">
                                    {visibleBooleanFields.map(fieldKey => (
                                        <div key={fieldKey} className="space-y-2 relative">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor={fieldKey}>{booleanFieldConfig[fieldKey].label}</Label>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveField(fieldKey, 'boolean')}
                                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                                    title="移除此欄位"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            <Input
                                                id={fieldKey}
                                                value={advancedSearchForm[fieldKey]}
                                                onChange={(e) => setAdvancedSearchForm(prev => ({
                                                    ...prev,
                                                    [fieldKey]: e.target.value
                                                }))}
                                                placeholder={booleanFieldConfig[fieldKey].placeholder}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* 新增欄位按鈕 */}
                                {visibleBooleanFields.length < Object.keys(booleanFieldConfig).length && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFieldSelectorType('boolean');
                                            setShowFieldSelector(true);
                                        }}
                                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors mt-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        新增欄位
                                    </button>
                                )}
                            </div>

                            {/* 分類檢索 */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">分類檢索</Label>
                                <div className="grid grid-cols-3 gap-4">
                                    {visibleClassificationFields.map(fieldKey => (
                                        <div key={fieldKey} className="space-y-2 relative">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor={fieldKey}>{classificationFieldConfig[fieldKey].label}</Label>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveField(fieldKey, 'classification')}
                                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                                    title="移除此欄位"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            <Input
                                                id={fieldKey}
                                                value={advancedSearchForm[fieldKey]}
                                                onChange={(e) => setAdvancedSearchForm(prev => ({
                                                    ...prev,
                                                    [fieldKey]: e.target.value
                                                }))}
                                                placeholder={classificationFieldConfig[fieldKey].placeholder}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* 新增欄位按鈕 */}
                                {visibleClassificationFields.length < Object.keys(classificationFieldConfig).length && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFieldSelectorType('classification');
                                            setShowFieldSelector(true);
                                        }}
                                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors mt-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        新增欄位
                                    </button>
                                )}
                            </div>

                            {/* 日期範圍與最大結果數 - 並排佈局 */}
                            <div className="grid grid-cols-2 gap-6 items-start">
                                {/* 日期範圍 */}
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold">日期範圍</Label>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="dateFrom">申請日起</Label>
                                            <Input
                                                id="dateFrom"
                                                type="date"
                                                value={advancedSearchForm.dateFrom}
                                                onChange={(e) => setAdvancedSearchForm(prev => ({
                                                    ...prev,
                                                    dateFrom: e.target.value
                                                }))}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="dateTo">申請日迄</Label>
                                            <Input
                                                id="dateTo"
                                                type="date"
                                                value={advancedSearchForm.dateTo}
                                                onChange={(e) => setAdvancedSearchForm(prev => ({
                                                    ...prev,
                                                    dateTo: e.target.value
                                                }))}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* 最大結果數設定 */}
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold">最大結果數</Label>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="maxResults">數量</Label>
                                            <Input
                                                id="maxResults"
                                                type="number"
                                                min="1"
                                                value={advancedSearchForm.maxResults === 100 ? '' : advancedSearchForm.maxResults}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setAdvancedSearchForm(prev => ({
                                                        ...prev,
                                                        maxResults: val === '' ? 100 : parseInt(val) || 100
                                                    }));
                                                }}
                                                placeholder="預設100,可輸入超過100以獲取更多結果"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>快速選擇</Label>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setAdvancedSearchForm(prev => ({ ...prev, maxResults: 30 }))}
                                                    className="flex-1"
                                                >
                                                    30
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setAdvancedSearchForm(prev => ({ ...prev, maxResults: 60 }))}
                                                    className="flex-1"
                                                >
                                                    60
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setAdvancedSearchForm(prev => ({ ...prev, maxResults: 100 }))}
                                                    className="flex-1"
                                                >
                                                    100
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end gap-3 p-6 border-t dark:border-gray-700">
                            <Button
                                variant="outline"
                                onClick={() => setShowAdvancedSearch(false)}
                            >
                                取消
                            </Button>
                            <Button
                                onClick={startAdvancedSearch}
                                disabled={isLoading}
                            >
                                {isLoading ? '檢索中...' : '開始檢索'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* 欄位選擇器對話框 */}
            <Dialog open={showFieldSelector} onOpenChange={setShowFieldSelector}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            選擇要新增的欄位
                        </DialogTitle>
                        <DialogDescription>
                            點擊欄位名稱即可新增到搜尋表單
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto py-4">
                        {fieldSelectorType === 'boolean' ? (
                            // 布林檢索可用欄位
                            Object.entries(booleanFieldConfig)
                                .filter(([key]) => !visibleBooleanFields.includes(key))
                                .map(([key, config]) => (
                                    <button
                                        key={key}
                                        onClick={() => {
                                            handleAddField(key);
                                            setShowFieldSelector(false);
                                        }}
                                        className="px-4 py-3 text-left border-2 border-gray-300 rounded-lg bg-white hover:bg-blue-50 hover:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
                                    >
                                        <div className="font-semibold text-gray-900">{config.label}</div>
                                        <div className="text-sm text-gray-600 mt-1">{config.placeholder}</div>
                                    </button>
                                ))
                        ) : (
                            // 分類檢索可用欄位
                            Object.entries(classificationFieldConfig)
                                .filter(([key]) => !visibleClassificationFields.includes(key))
                                .map(([key, config]) => (
                                    <button
                                        key={key}
                                        onClick={() => {
                                            handleAddField(key);
                                            setShowFieldSelector(false);
                                        }}
                                        className="px-4 py-3 text-left border-2 border-gray-300 rounded-lg bg-white hover:bg-blue-50 hover:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
                                    >
                                        <div className="font-semibold text-gray-900">{config.label}</div>
                                        <div className="text-sm text-gray-600 mt-1">{config.placeholder}</div>
                                    </button>
                                ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PatentDatabaseComparisonManager;